import store from '../state';
const API_BASE_URL = process.env.REACT_APP_USER_API_URL || 'https://api.jingleez.com';

// Helper function to handle API errors for fetch
const handleApiError = async (response) => {
  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    return Promise.reject({
      status: response.status,
      message: errorData.message || 'An error occurred',
      data: errorData
    });
  }
  return response.json();
};

// Helper function to get auth headers
const getAuthHeaders = () => {
  const state = store.getState();
  const token = state.auth?.token;
  
  const headers = {
    'Content-Type': 'application/json',
    'Api-Name': 'user-api'
  };
  
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }
  
  return headers;
};

// Get saved meal preferences for a user
const getUserPreferences = async (userId) => {
  try {
    const response = await fetch(`${API_BASE_URL}/meal-preferences?userId=${userId}`, {
      headers: getAuthHeaders()
    });
    return handleApiError(response);
  } catch (error) {
    return Promise.reject({
      message: error.message || 'Network error',
    });
  }
};

// Save user meal preferences
const saveUserPreferences = async (userId, preferences) => {
  try {
    const payload = {
      userId,
      ...preferences
    };
    const response = await fetch(`${API_BASE_URL}/meal-preferences`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(payload)
    });
    return handleApiError(response);
  } catch (error) {
    return Promise.reject({
      message: error.message || 'Network error',
    });
  }
};

// Generate a meal plan for a date range
const generateMealPlan = async (userId, startDate, endDate, notes, missingMeals, fillMissingOnly) => {
  try {
    // Format notes to be clearly visible
    const formattedNotes = notes ? 
      `User Preferences: ${notes}` : 
      '';
    
    // Build the basic payload
    const payload = {
      userId,
      startDate,
      endDate: endDate || startDate,
      notes: formattedNotes,
      userNotes: notes,
      additionalInstructions: notes,
      specificRequests: formattedNotes
    };
    
    // Add missing meals data if provided through parameters
    if (missingMeals && Array.isArray(missingMeals)) {
      payload.missingMeals = missingMeals;
      payload.fillMissingOnly = !!fillMissingOnly;
    }
    
    console.log('API Request payload:', JSON.stringify(payload));
    
    const response = await fetch(`${API_BASE_URL}/meal-plan`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(payload)
    });
    return handleApiError(response);
  } catch (error) {
    return Promise.reject({
      message: error.message || 'Network error',
    });
  }
};

// Get existing meal plans for a date range
const getMealPlans = async (userId, startDate, endDate) => {
  try {
    const params = new URLSearchParams({
      userId,
      startDate,
      ...(endDate && { endDate })
    });
    const response = await fetch(`${API_BASE_URL}/meal-plan?${params}`, {
      headers: getAuthHeaders()
    });
    return handleApiError(response);
  } catch (error) {
    return Promise.reject({
      message: error.message || 'Network error',
    });
  }
};

// Regenerate a specific meal
const regenerateMeal = async (userId, date, mealType, notes) => {
  try {
    // Format notes to explicitly mention the meal type
    const formattedNotes = notes ? 
      `For this ${mealType.toLowerCase()} meal: ${notes}` : 
      '';
    
    const payload = {
      userId,
      date,
      mealType,
      notes: formattedNotes,
      // Add these additional fields to make sure notes are included in prompt
      userNotes: notes,
      additionalInstructions: notes,
      specificRequests: formattedNotes
    };
    
    console.log(`Regenerating ${mealType} with notes: ${formattedNotes}`);
    
    const response = await fetch(`${API_BASE_URL}/meal-plan/regenerate`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(payload)
    });
    return handleApiError(response);
  } catch (error) {
    return Promise.reject({
      message: error.message || 'Network error',
    });
  }
};

// Get shopping list for a date range
const getShoppingList = async (userId, startDate, endDate, forceGenerate = false) => {
  try {
    const params = new URLSearchParams({
      userId,
      ...(startDate && { startDate }),
      ...(endDate && { endDate }),
      ...(forceGenerate && { forceGenerate: 'true' })
    });
    
    console.log(`Requesting shopping list with params:`, Object.fromEntries(params));
    
    const response = await fetch(`${API_BASE_URL}/shopping-list?${params}`, {
      headers: getAuthHeaders()
    });
    const data = await handleApiError(response);
    
    console.log('Raw API response for shopping list:', data);
    
    // Always use the requested date range when forceGenerate is true
    // Otherwise, use the API's dateRange for saved lists
    const dateRange = forceGenerate ? 
      { start: startDate || "", end: endDate || startDate || "" } :
      (data.dateRange || { start: startDate || "", end: endDate || startDate || "" });
    
    // Return the API response structure with consistent date range
    return {
      items: data.items || {},
      dateRange,
      status: data.status || 'success',
      listId: data.listId || null,
      fromSaved: data.fromSaved || false,
      message: data.message || null,
      forceGenerate // Include this flag in the response
    };
  } catch (error) {
    console.error('Error in mealPlannerService.getShoppingList:', error);
    return Promise.reject({
      message: error.message || 'Network error',
    });
  }
};

// Check if a shopping list exists for a user
const checkShoppingList = async (userId) => {
  try {
    const params = new URLSearchParams({ userId });
    
    console.log(`Checking if shopping list exists for user: ${userId}`);
    
    const response = await fetch(`${API_BASE_URL}/shopping-list/check?${params}`, {
      headers: getAuthHeaders()
    });
    const data = await handleApiError(response);
    
    console.log('Shopping list check response:', data);
    
    return {
      exists: data.exists || false,
      dateRange: data.dateRange || { start: "", end: "" },
      listId: data.listId || null
    };
  } catch (error) {
    console.error('Error in mealPlannerService.checkShoppingList:', error);
    return Promise.reject({
      message: error.message || 'Network error',
    });
  }
};

// Save a shopping list
const saveShoppingList = async (userId, items, dateRange) => {
  try {
    console.log(`Saving shopping list for user: ${userId}`);
    
    const payload = {
      userId,
      items,
      dateRange
    };
    
    const response = await fetch(`${API_BASE_URL}/shopping-list`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(payload)
    });
    
    const data = await handleApiError(response);
    
    console.log('Shopping list save response:', data);
    
    return {
      success: data.status === 'success',
      listId: data.listId || null,
      message: data.message || 'Shopping list saved successfully'
    };
  } catch (error) {
    console.error('Error in mealPlannerService.saveShoppingList:', error);
    return Promise.reject({
      message: error.message || 'Network error',
    });
  }
};

// Delete a shopping list
const deleteShoppingList = async (userId, listId) => {
  try {
    console.log(`Deleting shopping list: ${listId} for user: ${userId}`);
    
    const params = new URLSearchParams({ userId });
    
    const response = await fetch(`${API_BASE_URL}/shopping-list/${listId}?${params}`, {
      method: 'DELETE',
      headers: getAuthHeaders()
    });
    
    const data = await handleApiError(response);
    
    console.log('Shopping list delete response:', data);
    
    return {
      success: data.status === 'success',
      message: data.message || 'Shopping list deleted successfully'
    };
  } catch (error) {
    console.error('Error in mealPlannerService.deleteShoppingList:', error);
    return Promise.reject({
      message: error.message || 'Network error',
    });
  }
};

// Remove an item from the shopping list
const removeShoppingListItem = async (userId, item, listId) => {
  try {
    console.log(`Removing item from shopping list for user: ${userId}`);
    
    const payload = {
      userId,
      item,
      listId
    };
    
    const response = await fetch(`${API_BASE_URL}/shopping-list/remove-item`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(payload)
    });
    
    const data = await handleApiError(response);
    
    console.log('Item removal response:', data);
    
    return {
      success: data.status === 'success',
      items: data.items || {},
      message: data.message || 'Item removed successfully'
    };
  } catch (error) {
    console.error('Error in mealPlannerService.removeShoppingListItem:', error);
    return Promise.reject({
      message: error.message || 'Network error',
    });
  }
};

const mealPlannerService = {
  getUserPreferences,
  saveUserPreferences,
  generateMealPlan,
  getMealPlans,
  regenerateMeal,
  getShoppingList,
  checkShoppingList,
  saveShoppingList,
  deleteShoppingList,
  removeShoppingListItem
};

export default mealPlannerService; 